import { useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { getDD_Month_Time } from 'utils/date';
import Tab from 'widgets/tab';
import { ModalMobile, ModalDesktop } from '@acko-tech/building-blocks.ui.modal';
import TransactionDetailsModal from './detailsModal';
import TransactionItemCard from './itemCard';
import SnackBar from 'library/snackbar';
import { useResponsiveDevice } from 'hooks/useResponsiveDevice';
import { IOrder, IOrderDetails } from '../types';
import { getImageBasePath } from 'utils/generics';
import * as Styles from './styles';
import { ModalLoader } from 'utils/styles';
import { KycStatus, OrderStatusLabel, ProposalStatus } from './constants';
import { useRouter } from 'next/router';

enum TabOptions {
    PENDING = 'Pending',
    COMPLETED = 'Completed',
}

interface IProps {
    pendingOrders: IOrder[];
    completedOrders: IOrder[];
    hasMorePending: boolean;
    hasMoreCompleted: boolean;
    onLoadMore: any;
}

function OrderHistory({
    pendingOrders,
    completedOrders,
    hasMorePending,
    hasMoreCompleted,
    onLoadMore,
}: IProps) {
    const { isMobile } = useResponsiveDevice();
    const router = useRouter();

    const [selectedOrder, setSelectedOrder] = useState<IOrderDetails | null>(
        null
    );
    const [selectedTab, setSelectedTab] = useState(TabOptions.PENDING);
    const [modalStatus, setModalStatus] = useState(false);
    const [showLoader, setShowLoader] = useState(false);
    const [isBtnLoading, setIsBtnLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({
        type: '',
        message: '',
    });

    const onSelectTab = (option: TabOptions) => {
        setSelectedTab(option);
    };

    const getActiveOrdersList = () => {
        if (selectedTab === TabOptions.COMPLETED) {
            return completedOrders;
        }
        return pendingOrders;
    };

    const onClickOrder = (order) => {
        setModalStatus(true);
        setShowLoader(true);
        import('./apis').then(async (req) => {
            const { status, data } = await req.getOrder(order?.order_id);

            if (status && data) {
                setSelectedOrder(data);
                setShowLoader(false);
            }
        });
    };

    const orderStatus = (status: string) => {
        switch (status) {
            case 'FAILED':
                return {
                    text: 'Failed',
                    color: '#D83D37',
                    background: '#FBEAEA',
                };
            case 'PENDING_VERIFICATION':
                return {
                    text: 'Pending Verification',
                    color: '#F58700',
                    background: '#FFF7E5',
                };
            case 'PAYMENT_PENDING':
                return {
                    text: 'Payment Pending',
                    color: '#5920C5',
                    background: '#EFE9FB',
                };
            default:
                return {
                    text: 'Policy Issued',
                    color: '#0FA457',
                    background: '#EBFBEE',
                };
        }
    };

    const getValueObjects = (order: IOrder) => {
        const { text, color, background } = orderStatus(order.status);
        return {
            value1: order?.data?.name,
            value2: `₹${order?.amount}`,
            value3: order?.data?.mmv,
            value4: order?.data?.phone,
            value5: text,
            labelColor: {
                color,
                background,
            },
            value6: getDD_Month_Time(new Date(order.created_on)),
        };
    };

    const onClick = () => {
        import('./apis').then(async (req) => {
            setIsBtnLoading(true);
            const { status, statusCode, errorMessage } =
                await req.resendLinkToCustomer(
                    selectedOrder?.proposal?.proposal_id || ''
                );
            if (statusCode === 401) {
                router.push(`/login`);
                setSnackbar({
                    type: 'failure',
                    message: 'Token expired. Redirecting to login page.',
                });
                return;
            }
            setIsBtnLoading(false);
            if (status) {
                setSnackbar({
                    type: 'success',
                    message: 'Payment link has sent successfully.',
                });
                return;
            }
            setSnackbar({
                type: 'failure',
                message: errorMessage || 'Oops. Something went wrong.',
            });
        });
    };

    const getStatusMessage = (status) => {
        let statusValue = ProposalStatus[status?.policy_status];

        if (statusValue === 'Payment done') {
            statusValue = KycStatus[status?.parameters?.kyc_status];
        }

        const label = OrderStatusLabel[statusValue];

        return label;
    };

    const Modal: any = isMobile ? ModalMobile : ModalDesktop;

    return (
        <Styles.Wrapper>
            <Styles.AllTransactionText>
                Recent transactions
            </Styles.AllTransactionText>
            <Styles.TabWrapper>
                <Tab
                    items={[TabOptions.PENDING, TabOptions.COMPLETED]}
                    color={'#0FA457'}
                    selectedTab={selectedTab}
                    onSelectTab={onSelectTab}
                />
            </Styles.TabWrapper>
            <div>
                {getActiveOrdersList().length === 0 ? (
                    <Styles.NoResult>No transactions</Styles.NoResult>
                ) : (
                    <InfiniteScroll
                        dataLength={getActiveOrdersList().length}
                        next={() => onLoadMore(selectedTab.toLowerCase())}
                        hasMore={
                            selectedTab === TabOptions.PENDING
                                ? hasMorePending
                                : hasMoreCompleted
                        }
                        loader={<></>}
                    >
                        {getActiveOrdersList().map((order, index) => (
                            <Styles.ItemCardWrapper
                                key={order.ekey}
                                onClick={() => onClickOrder(order)}
                            >
                                <TransactionItemCard
                                    {...getValueObjects(order)}
                                />
                                {index !== getActiveOrdersList().length - 1 && (
                                    <Styles.Hr />
                                )}
                            </Styles.ItemCardWrapper>
                        ))}
                    </InfiniteScroll>
                )}
            </div>
            <Modal
                header={<></>}
                show={modalStatus}
                onHide={() => setModalStatus(false)}
                padding={'20px'}
                wrapperStyles={`border-radius: 16px 16px 0px 0px;`}
                showOverlayBackground={true}
            >
                {showLoader ? (
                    <Styles.ModalLoaderWrapper>
                        <ModalLoader
                            src={getImageBasePath('/gif/pageLoader.gif')}
                        />
                    </Styles.ModalLoaderWrapper>
                ) : (
                    <TransactionDetailsModal
                        selectedTab={selectedTab}
                        customerName={
                            selectedOrder?.proposal?.users?.[0]?.name || ''
                        }
                        variantName={
                            selectedOrder?.proposal?.insured?.[0]?.variant || ''
                        }
                        lastUpdatedAt={
                            selectedOrder?.proposal?.meta?.updated_on || ''
                        }
                        status={
                            selectedOrder?.proposal?.status?.policy_status || ''
                        }
                        statusMessage={getStatusMessage(
                            selectedOrder?.proposal?.status
                        )}
                        planName={
                            selectedOrder?.proposal?.plans?.[0]?.plan_name || ''
                        }
                        mobileNumber={
                            selectedOrder?.proposal?.users?.[0].phone || ''
                        }
                        amount={
                            selectedOrder?.proposal?.premium?.plan?.[0]?.insured
                                ?.gross_premium || 0
                        }
                        onClick={onClick}
                        isBtnLoading={isBtnLoading}
                        vin={
                            selectedOrder?.proposal?.insured?.[0]
                                ?.chassis_number
                        }
                        redirectUrl={
                            selectedTab === TabOptions?.PENDING
                                ? selectedOrder?.proposal?.meta?.redirect_url ||
                                  ''
                                : null
                        }
                    />
                )}
            </Modal>
            <SnackBar
                {...snackbar}
                handleSnakbarComplete={() => {
                    setSnackbar({ type: '', message: '' });
                }}
            />
        </Styles.Wrapper>
    );
}

export default OrderHistory;
